import React from "react";
import { Activity, Calendar, Receipt, ArrowRight, RefreshCw, AlertCircle, FileText, Loader2, Award, ChevronRight, Info } from "lucide-react";

/**
 * GuestPortalContent - Main view area rendering the dashboard, stay history,
 * and invoices tabs. Receives all data, filters, and handlers as props.
 *
 * @param {Object} props - Data, filter state, and handlers from GuestPortalLayout.
 * @returns {React.ReactElement} The rendered main content area.
 */
export default function GuestPortalContent({
  activeTab, setActiveTab, loyaltyPoints, reservations, invoices, transactions,
  isLoadingReservations, isLoadingInvoices, searchTerm, setSearchTerm,
  debouncedSearch, statusFilter, setStatusFilter, currentPage, setCurrentPage,
  downloadPdf, setSelectedInvoice, setCorrectionForm, fetchReservations,
  fetchInvoices, currentUser, authUser, upcomingReservations, t,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  loyaltyPoints: number;
  reservations: any[];
  invoices: any[];
  transactions: any[];
  isLoadingReservations: boolean;
  isLoadingInvoices: boolean;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  debouncedSearch: string;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  downloadPdf: (invoiceId: string, invoiceNumber: string) => Promise<void> | void;
  setSelectedInvoice: (invoice: any) => void;
  setCorrectionForm: React.Dispatch<React.SetStateAction<any>>;
  fetchReservations: () => Promise<void> | void;
  fetchInvoices: () => Promise<void> | void;
  currentUser: any;
  authUser: any;
  upcomingReservations: any[];
  t: (key: string, opts?: any) => string;
}) {
  return (
    <main className="flex-1 p-4 md:p-8 max-w-6xl mx-auto w-full animate-in fade-in">

      {/* --- VIEW: DASHBOARD --- */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {/* Greeting Header */}
          <div className="glass-card p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-black display-font uppercase tracking-tight">
                {t("guestPortal.greeting", { name: authUser?.user_metadata?.first_name || currentUser?.name || t("guestPortal.guest") })}
              </h2>
              <p className="text-xs text-muted mt-1">
                {t("guestPortal.dashboardDescription")}
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab("reservations")}
                className="hub-btn hub-btn-primary"
              >
                {t("guestPortal.viewStays")} <ChevronRight size={14} />
              </button>
            </div>
          </div>

          {/* Quick Stats Row — computed from data already in props (no extra fetches) */}
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {[
              { icon: Calendar, label: t("guestPortal.statTotalStays"), value: reservations.length },
              { icon: ChevronRight, label: t("guestPortal.statUpcoming"), value: upcomingReservations.length },
              { icon: Receipt, label: t("guestPortal.statInvoices"), value: invoices.length },
            ].map(({ icon: StatIcon, label, value }) => (
              <button
                key={label}
                onClick={() => setActiveTab(label === t("guestPortal.statInvoices") ? "invoices" : "reservations")}
                className="glass-card p-4 md:p-5 flex flex-col items-start gap-1 hover:border-accent hover:shadow-md transition-all cursor-pointer text-left group"
              >
                <span className="w-8 h-8 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                  <StatIcon size={15} />
                </span>
                <span className="text-xl md:text-2xl font-black text-fg leading-none">{value}</span>
                <span className="text-[9px] font-black uppercase tracking-widest text-muted">{label}</span>
              </button>
            ))}
          </div>

          {/* Loyalty and Next Stay Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Loyalty Card View — brand-accent gradient (no off-palette colors) */}
            <div className="relative overflow-hidden rounded-4xl p-6 md:p-8 text-white shadow-2xl flex flex-col justify-between min-h-[240px] border border-accent"
              style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--accent) 88%, black) 0%, var(--accent) 45%, color-mix(in srgb, var(--accent) 70%, white) 100%)" }}>
              {/* Glossy Aura */}
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-[60px]" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />

              <div className="flex justify-between items-start">
                <div>
                  <span className="text-[9px] font-black uppercase tracking-[0.25em] text-white/70">
                    {t("guestPortal.loyaltyCard")}
                  </span>
                  <h3 className="text-lg font-black uppercase display-font mt-0.5 tracking-wider">
                    AdaptHub Club
                  </h3>
                </div>
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center border border-white/25">
                  <Award size={20} className="text-white animate-pulse" />
                </div>
              </div>

              <div className="my-4">
                <p className="text-[10px] uppercase text-white/70 tracking-wider">{t("guestPortal.totalPoints")}</p>
                <p className="text-3xl font-black tracking-tight">{t("guestPortal.pointsDisplay", { points: loyaltyPoints })}</p>
                {/* Tier progress — visual anchor for the next reward level.
                    Thresholds mirror the tier badge logic: 250 → Silver, 1000 → Gold. */}
                <div className="mt-3">
                  <div className="h-1.5 w-full rounded-full bg-white/20 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-white transition-all duration-700"
                      style={{ width: `${Math.min(100, (loyaltyPoints / 1000) * 100)}%` }}
                    />
                  </div>
                  <p className="text-[9px] font-bold text-white/80 mt-1.5 tracking-wide">
                    {loyaltyPoints >= 1000
                      ? t("guestPortal.tierMaxed")
                      : t("guestPortal.pointsToNextTier", {
                        points: Math.ceil((loyaltyPoints >= 250 ? 1000 : 250) - loyaltyPoints),
                        tier: loyaltyPoints >= 250 ? t("guestPortal.gold") : t("guestPortal.silver"),
                      })}
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[8px] uppercase text-white/60 tracking-widest">{t("guestPortal.member")}</p>
                  <p className="text-[10px] font-black tracking-wider truncate max-w-[200px]">{authUser?.email}</p>
                </div>
                <span className="px-3 py-1 bg-white/15 border border-white/25 rounded-lg text-[9px] font-black uppercase tracking-widest">
                  {loyaltyPoints >= 1000 ? t("guestPortal.gold") : loyaltyPoints >= 250 ? t("guestPortal.silver") : t("guestPortal.bronze")}
                </span>
              </div>
            </div>

            {/* Next Upcoming Reservation — accent top-border highlight */}
            <div className="glass-card p-6 md:p-8 flex flex-col justify-between border-t-2 border-t-accent">
              <div>
                <h3 className="text-xs font-black uppercase tracking-wider text-muted mb-4">
                  {t("guestPortal.nextStay")}
                </h3>
                {upcomingReservations.length > 0 ? (
                  (() => {
                    const next = upcomingReservations[0];
                    return (
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <h4 className="text-base font-bold text-fg">
                            {next.propertyName || t("guestPortal.fallbackHotelName")}
                          </h4>
                          <span className="px-2.5 py-1 bg-accent-muted text-accent text-[8px] font-black rounded-lg uppercase">
                            {next.status === "InHouse" ? t("guestPortal.active") : t("guestPortal.confirmed")}
                          </span>
                        </div>
                        <div className="text-xs text-muted space-y-1">
                          <p><strong>{t("guestPortal.checkIn")}</strong> {new Date(next.arrival).toLocaleDateString()}</p>
                          <p><strong>{t("guestPortal.checkOut")}</strong> {new Date(next.departure).toLocaleDateString()}</p>
                          <p><strong>{t("guestPortal.room")}</strong> {next.unitName || t("guestPortal.assignmentAtArrival")}</p>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-6 text-muted space-y-2">
                    <Calendar size={24} className="mx-auto opacity-40" />
                    <p className="text-xs">{t("guestPortal.noUpcomingStays")}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-border flex justify-between items-center text-xs">
                <span className="text-[10px] font-bold text-muted">{t("guestPortal.wantToBook")}</span>
                <a
                  href="https://adaptapartments.de"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] font-black text-accent uppercase tracking-wider hover:underline inline-flex items-center gap-1"
                >
                  {t("guestPortal.toWebsite")} <ArrowRight size={12} />
                </a>
              </div>
            </div>
          </div>

          {/* Loyalty Transactions Log */}
          <div className="glass-card p-6 md:p-8">
            <h3 className="text-xs font-black uppercase tracking-wider text-muted mb-4 flex items-center gap-2">
              <Activity size={16} className="text-accent" /> {t("guestPortal.pointHistory")}
            </h3>
            {transactions.length > 0 ? (
              <div className="divide-y divide-border max-h-60 overflow-y-auto pr-2">
                {transactions.map((tx: any) => (
                  <div key={tx.id} className="py-3 flex justify-between items-center text-xs">
                    <div>
                      <p className="font-bold text-fg">{tx.description}</p>
                      <p className="text-[9px] text-muted mt-0.5">{tx.createdAt}</p>
                    </div>
                    <span className="font-black text-accent font-mono">
                      +{tx.amountEarned} {t("guestPortal.pointsUnit")}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted space-y-2">
                <Award size={28} className="mx-auto opacity-30" />
                <p className="text-xs">{t("guestPortal.noPointsYet")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- VIEW: STAY HISTORY --- */}
      {activeTab === "reservations" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-black display-font uppercase tracking-tight">
                {t("guestPortal.staysAndBookings")}
              </h2>
              <p className="text-xs text-muted mt-1">
                {t("guestPortal.staysDescription")}
              </p>
            </div>
            <button
              onClick={fetchReservations}
              disabled={isLoadingReservations}
              className="p-2 hover:bg-surface border border-border rounded-xl transition-all"
              title={t("guestPortal.refresh")}
            >
              <RefreshCw size={14} className={isLoadingReservations ? "animate-spin" : ""} />
            </button>
          </div>

          {/* Stay Filters & Search Bar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder={t("guestPortal.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-xs font-black uppercase tracking-wider bg-surface border border-border rounded-xl py-2.5 px-4 focus:outline-hidden focus:ring-2 focus:ring-accent/20 transition-all shadow-xs"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs font-black uppercase tracking-wider bg-surface border border-border rounded-xl py-2.5 px-4 focus:outline-hidden"
            >
              <option value="All">{t("guestPortal.allStatuses")}</option>
              <option value="Confirmed">{t("guestPortal.confirmed")}</option>
              <option value="InHouse">{t("guestPortal.inhouse")}</option>
              <option value="CheckedOut">{t("guestPortal.checkedOut")}</option>
              <option value="Canceled">{t("guestPortal.cancelled")}</option>
            </select>
          </div>

          {isLoadingReservations ? (
            <div className="text-center py-16">
              <Loader2 className="animate-spin mx-auto text-accent mb-4" size={32} />
              <p className="text-xs text-muted font-black uppercase tracking-wider">{t("guestPortal.loadingBookings")}</p>
            </div>
          ) : reservations.length > 0 ? (
            (() => {
              const filteredReservations = reservations.filter((res: any) => {
                const matchesSearch =
                  res.id.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                  (res.propertyName || "").toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                  (res.unitName || "").toLowerCase().includes(debouncedSearch.toLowerCase());

                const matchesStatus =
                  statusFilter === "All" || res.status === statusFilter;

                return matchesSearch && matchesStatus;
              });

              if (filteredReservations.length === 0) {
                return (
                  <div className="glass-card p-12 text-center text-muted space-y-4">
                    <AlertCircle size={36} className="mx-auto opacity-30 text-accent" />
                    <div>
                      <p className="font-bold">{t("guestPortal.noMatchingStays")}</p>
                      <p className="text-xs max-w-sm mx-auto mt-1">
                        {t("guestPortal.noMatchingStaysDescription")}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setStatusFilter("All");
                      }}
                      className="px-4 py-2 bg-bg border border-border rounded-xl text-[10px] font-black uppercase tracking-wider hover:border-accent transition-all cursor-pointer"
                    >
                      {t("guestPortal.resetFilters")}
                    </button>
                  </div>
                );
              }

              // Sort by arrival date descending (newest reservation always at the top)
              const sortedReservations = [...filteredReservations].sort((a, b) => new Date(b.arrival).getTime() - new Date(a.arrival).getTime());

              // Pagination logic (itemsPerPage = 6)
              const itemsPerPage = 6;
              const totalPages = Math.ceil(sortedReservations.length / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const paginatedReservations = sortedReservations.slice(startIndex, startIndex + itemsPerPage);

              return (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in">
                    {paginatedReservations.map((res) => {
                      const checkIn = new Date(res.arrival).toLocaleDateString();
                      const checkOut = new Date(res.departure).toLocaleDateString();

                      return (
                        <div key={res.id} className="glass-card p-5 flex flex-col justify-between hover:border-accent transition-colors">
                          <div>
                            <div className="flex justify-between items-start gap-2 mb-3">
                              <div>
                                <h3 className="font-bold text-sm text-fg">
                                  {res.propertyName || t("guestPortal.fallbackHotelName")}
                                </h3>
                                <p className="text-[9px] text-muted font-bold mt-0.5 uppercase tracking-wider">
                                  {t("guestPortal.code")} {res.id}
                                </p>
                              </div>
                              <span className={`px-2 py-0.5 text-[8px] font-black rounded-lg uppercase ${res.status === "CheckedOut" ? "bg-slate-100 text-slate-500" :
                                res.status === "InHouse" ? "bg-emerald-50 text-emerald-600 border border-emerald-100" :
                                  res.status === "Canceled" ? "bg-rose-50 text-rose-500 border border-rose-100" :
                                    "bg-indigo-50 text-indigo-600 border border-indigo-100"
                                }`}>
                                {res.status === "CheckedOut" ? t("guestPortal.checkedOut") :
                                  res.status === "InHouse" ? t("guestPortal.inhouse") :
                                    res.status === "Canceled" ? t("guestPortal.cancelled") : t("guestPortal.confirmed")}
                              </span>
                            </div>

                            <div className="text-xs space-y-1.5 text-muted mb-4 bg-bg p-3 rounded-xl border border-border">
                              <p className="flex justify-between">
                                <span>{t("guestPortal.arrival")}</span>
                                <span className="font-bold text-fg">{checkIn}</span>
                              </p>
                              <p className="flex justify-between">
                                <span>{t("guestPortal.departure")}</span>
                                <span className="font-bold text-fg">{checkOut}</span>
                              </p>
                              <p className="flex justify-between">
                                <span>{t("guestPortal.room")}</span>
                                <span className="font-bold text-fg">{res.unitName || t("guestPortal.na")}</span>
                              </p>
                            </div>
                          </div>

                          <div className="flex justify-between items-center text-xs">
                            <div>
                              <p className="text-[9px] uppercase text-muted">{t("guestPortal.totalAmount")}</p>
                              <p className="font-black text-sm text-fg">
                                {typeof res.totalGrossAmount?.amount === "number" ? res.totalGrossAmount.amount.toFixed(2) : t("guestPortal.na")} {res.totalGrossAmount?.currency || "EUR"}
                              </p>
                            </div>
                            {res.status === "CheckedOut" && (
                              <button
                                onClick={() => setActiveTab("invoices")}
                                className="text-[9px] font-black text-accent uppercase tracking-wider hover:underline"
                              >
                                {t("guestPortal.viewInvoice")}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t border-border animate-in fade-in">
                      <span className="text-[10px] font-black uppercase tracking-wider text-muted">
                        {t("guestPortal.showingEntries", { start: startIndex + 1, end: Math.min(startIndex + itemsPerPage, sortedReservations.length), total: sortedReservations.length })}
                      </span>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage((prev: number) => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1.5 bg-surface border border-border rounded-xl text-[10px] font-black uppercase tracking-wider text-fg hover:border-accent hover:text-accent disabled:opacity-40 disabled:hover:border-border disabled:hover:text-fg transition-all cursor-pointer"
                        >
                          {t("guestPortal.back")}
                        </button>

                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`w-7 h-7 rounded-lg text-[9px] font-black transition-all cursor-pointer ${currentPage === page
                                ? "bg-accent text-white shadow"
                                : "bg-surface border border-border text-fg hover:border-accent hover:text-accent"
                                }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={() => setCurrentPage((prev: number) => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1.5 bg-surface border border-border rounded-xl text-[10px] font-black uppercase tracking-wider text-fg hover:border-accent hover:text-accent disabled:opacity-40 disabled:hover:border-border disabled:hover:text-fg transition-all cursor-pointer"
                        >
                          {t("guestPortal.forward")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="glass-card p-12 text-center text-muted space-y-4">
              <Calendar size={48} className="mx-auto opacity-30" />
              <div>
                <p className="font-bold">{t("guestPortal.noStaysFound")}</p>
                <p className="text-xs max-w-sm mx-auto mt-1">
                  {t("guestPortal.noStaysFoundDescription", { email: authUser?.email })}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- VIEW: INVOICES --- */}
      {activeTab === "invoices" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-black display-font uppercase tracking-tight">
                {t("guestPortal.invoicesAndDocuments")}
              </h2>
              <p className="text-xs text-muted mt-1">
                {t("guestPortal.invoicesDescription")}
              </p>
            </div>
            <button
              onClick={fetchInvoices}
              disabled={isLoadingInvoices}
              className="p-2 hover:bg-surface border border-border rounded-xl transition-all"
              title={t("guestPortal.refresh")}
            >
              <RefreshCw size={14} className={isLoadingInvoices ? "animate-spin" : ""} />
            </button>
          </div>

          {/* GoBD Compliance Notice */}
          <div className="p-4 bg-amber-50 border border-amber-100 text-amber-800 rounded-2xl flex gap-3 text-xs leading-relaxed shadow-xs">
            <Info size={20} className="shrink-0 text-amber-600 mt-0.5" />
            <div>
              <p className="font-bold">{t("guestPortal.gobdTitle")}</p>
              <p className="mt-0.5">
                {t("guestPortal.gobdText")}
              </p>
            </div>
          </div>

          {isLoadingInvoices ? (
            <div className="text-center py-16">
              <Loader2 className="animate-spin mx-auto text-accent mb-4" size={32} />
              <p className="text-xs text-muted font-black uppercase tracking-wider">{t("guestPortal.loadingInvoices")}</p>
            </div>
          ) : invoices.length > 0 ? (
            <div className="glass-card overflow-hidden border border-border shadow-md">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-bg border-b border-border text-[9px] font-black uppercase tracking-widest text-muted">
                      <th className="p-4">{t("guestPortal.invoiceNumber")}</th>
                      <th className="p-4">{t("guestPortal.date")}</th>
                      <th className="p-4">{t("guestPortal.reservation")}</th>
                      <th className="p-4 text-right">{t("guestPortal.amount")}</th>
                      <th className="p-4 text-center">{t("guestPortal.actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border text-xs">
                    {invoices.map((inv: any) => (
                      <tr key={inv.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-4 font-bold text-fg">
                          {inv.invoiceNumber || inv.id}
                        </td>
                        <td className="p-4">
                          {inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : t("guestPortal.na")}
                        </td>
                        <td className="p-4 font-bold text-muted">
                          {inv.reservationId}
                        </td>
                        <td className="p-4 text-right font-black">
                          {inv.balance?.amount?.toFixed(2) || "0.00"} {inv.balance?.currency || "EUR"}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() => downloadPdf(inv.id, inv.invoiceNumber)}
                              className="px-3 py-1.5 bg-bg border border-border text-[10px] font-black uppercase tracking-wider rounded-lg hover:border-accent hover:text-accent transition-all flex items-center gap-1.5 cursor-pointer"
                            >
                              <FileText size={12} /> {t("guestPortal.pdf")}
                            </button>
                            <button
                              onClick={() => {
                                setSelectedInvoice(inv);
                                setCorrectionForm((prev: any) => ({
                                  ...prev,
                                  originalCompanyName: ""
                                }));
                              }}
                              className="px-3 py-1.5 bg-indigo-50 border border-indigo-100 text-indigo-600 text-[10px] font-black uppercase tracking-wider rounded-lg hover:bg-indigo-100 transition-all cursor-pointer"
                            >
                              {t("guestPortal.correction")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="glass-card p-12 text-center text-muted space-y-4">
              <Receipt size={48} className="mx-auto opacity-30" />
              <div>
                <p className="font-bold">{t("guestPortal.noInvoices")}</p>
                <p className="text-xs max-w-sm mx-auto mt-1">
                  {t("guestPortal.noInvoicesDescription")}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
